import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  VStack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Button,
  Input,
  Select,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Checkbox,
  useDisclosure,
  useToast,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import Chart from "react-apexcharts";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";
import SendEmail from "../../components/SendEmail";
import { fetchWithToken } from "../../lib/api";
import { QuestionIcon } from "@chakra-ui/icons";
function getBaseDate() {
  const today = new Date();
  today.setDate(today.getDate() - 3);
  return today;
}

// 기본 기간 반환 함수
function getDefaultDates(period = 7) {
  const base = getBaseDate();
  const end = base.toISOString().slice(0, 10);
  const startDateObj = new Date(base);
  startDateObj.setDate(base.getDate() - period);
  const start = startDateObj.toISOString().slice(0, 10);
  return { start, end };
}

const Dashboard = () => {
  const { user } = useAuth();
  const [inputDateRange, setInputDateRange] = useState(getDefaultDates(7));
  const [dateRange, setDateRange] = useState(getDefaultDates(7));
  const [period, setPeriod] = useState(7); // 7, 14, 21, 30
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chart1Data, setChart1Data] = useState({
    categories: [],
    revenue: [],
    views: [],
    premiumViews: [],
  });
  const [hasContract, setHasContract] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // 상담 모달 상태 및 폼
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    channel: "",
    agree: false,
  });
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const isFormValid =
    form.name && form.email && form.phone && form.channel && form.agree;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoadingModal(true);
    try {
      await SendEmail({
        email: "contact@plpl.kr",
        ccEmails: user?.email ? [user.email] : [],
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "jaeeui.kwon@rhoonart.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "blair.park@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "kirby.lee@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      toast({
        title: "상담 요청이 접수되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setForm({ name: "", email: "", phone: "", channel: "", agree: false });
      onClose();
    } catch (e) {
      toast({
        title: "상담 요청에 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingModal(false);
    }
  };

  useEffect(() => {
    const checkContract = async () => {
      if (!user?.email) {
        setHasContract(false);
        setIsInitialLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from("shorts_contract")
          .select("id")
          .eq("contractor", user.email)
          .maybeSingle();
        if (error) {
          setHasContract(false);
          setIsInitialLoading(false);
          return;
        }
        setHasContract(!!data);
        setIsInitialLoading(false);
      } finally {
        // setIsInitialLoading(false); // 위에서 처리
      }
    };
    checkContract();
  }, [user]);

  useEffect(() => {
    if (hasContract !== true) return;
    const fetchSummary = async () => {
      if (!user?.email) return;
      setLoading(true);
      setError("");
      try {
        // 1. user 테이블에서 id 조회
        const { data, error } = await supabase
          .from("user")
          .select("id")
          .eq("email", user.email)
          .single();
        if (error || !data?.id) throw new Error("user_id 조회 실패");
        const userId = data.id;
        // 2. access_token 가져오기
        const accessToken = localStorage.getItem("access_token");
        // 3. API 요청
        const params = new URLSearchParams({
          user_id: userId,
          start_date: dateRange.start,
          end_date: dateRange.end,
        });
        const response = await fetchWithToken(
          `https://plpl.rhoonart.co.kr/profits/total?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        if (!result?.data?.summary) throw new Error("데이터 없음");
        setSummary(result.data.summary);
        // dailyStats 차트 데이터 가공
        const dailyStats = result?.data?.dailyStats || [];
        setChart1Data({
          categories: dailyStats.map((d) => d.date),
          revenue: dailyStats.map((d) => d.revenueKrw),
          views: dailyStats.map((d) => d.views),
          premiumViews: dailyStats.map((d) => d.premiumViews),
        });
        setChart2Data({
          categories: dailyStats.map((d) => d.date),
          watchTime: dailyStats.map((d) => Math.round(d.watchTime ?? 0)),
          premiumWatchTime: dailyStats.map((d) =>
            Math.round(d.premiumWatchTime ?? 0)
          ),
        });
      } catch (e) {
        setError(e.message || "데이터 로드 실패");
        setSummary(null);
        setChart1Data({
          categories: [],
          revenue: [],
          views: [],
          premiumViews: [],
        });
        setChart2Data({
          categories: [],
          watchTime: [],
          premiumWatchTime: [],
        });
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [user, dateRange, hasContract]);

  // 로딩 상태 체크 함수 추가
  const isLoading = isInitialLoading;

  // 카드 데이터
  const stats = [
    {
      label: "총 수익 (KRW)",
      value: summary?.totalRevenueKrw
        ? `₩${summary.totalRevenueKrw.toLocaleString()}`
        : "-",
    },
    {
      label: "총 수익 (USD)",
      value: summary?.totalRevenueUsd
        ? `$ ${summary.totalRevenueUsd.toLocaleString()}`
        : "-",
    },
    {
      label: "총 영상 수",
      value: summary?.totalVideos
        ? `${summary.totalVideos.toLocaleString()}개`
        : "-",
    },
    {
      label: "총 조회수",
      value: summary?.totalViews
        ? `${summary.totalViews.toLocaleString()}회`
        : "-",
      hasTooltip: true,
    },
  ];

  // 첫 번째 차트 데이터 (실제 데이터)
  const chart1 = {
    options: {
      chart: { id: "revenue-views", toolbar: { show: true } },
      xaxis: { categories: chart1Data.categories },
      colors: ["#1e90ff", "#00e676", "#ffc107"],
      stroke: { width: 4 },
      legend: { show: true, position: "bottom" },
    },
    series: [
      { name: "수익(KRW)", data: chart1Data.revenue },
      { name: "조회수", data: chart1Data.views },
      { name: "[프리미엄] 조회수", data: chart1Data.premiumViews },
    ],
  };

  // 두 번째 차트 데이터 (시청 시간, 프리미엄 시청 시간)
  const [chart2Data, setChart2Data] = useState({
    categories: [],
    watchTime: [],
    premiumWatchTime: [],
  });

  const chart2 = {
    options: {
      chart: { id: "watchtime", toolbar: { show: true } },
      xaxis: {
        categories: chart2Data.categories,
      },
      colors: ["#7c3aed", "#e040fb"],
      stroke: { width: 4 },
      legend: { show: true, position: "bottom" },
    },
    series: [
      {
        name: "시청 시간",
        data: chart2Data.watchTime,
      },
      {
        name: "[프리미엄] 시청 시간",
        data: chart2Data.premiumWatchTime,
      },
    ],
  };

  // 기간 변경 핸들러
  const handlePeriodChange = (days) => {
    setPeriod(days);
    setInputDateRange(getDefaultDates(days));
  };

  // 검색 버튼 클릭 시
  const handleSearch = () => {
    setDateRange(inputDateRange);
  };

  if (isLoading) {
    return (
      <Flex minH="60vh" align="center" justify="center" direction="column">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }

  if (hasContract === false) {
    return (
      <Flex minH="60vh" direction="column" align="center" justify="center">
        <Text fontSize="md" color="gray.700" mb={2}>
          신규 회원은 레이블리와 계약 체결 후 이용이 가능합니다.
        </Text>
        <Button colorScheme="yellow" size="lg" mt={2} onClick={onOpen}>
          계약 상담 신청하기
        </Button>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent maxW="lg">
            <ModalHeader>레이블리 이용 계약 문의하기</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4} fontSize="md">
                신규 회원은 개별 계약 후 이용이 가능합니다. 이용 계약 상담을
                신청해주시면, 기재해주신 연락처로 연락드리겠습니다.
                <br />
                (이메일만 기재할 경우, 스팸으로 분류되는 경우가 많습니다. 이용
                계약 문의주시면, 연락처로 연락드립니다)
              </Text>
              <FormControl mb={3} isRequired>
                <FormLabel>성함 (필수)</FormLabel>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="성함"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>이메일 (필수)</FormLabel>
                <Input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="이메일"
                  type="email"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>연락처 (필수)</FormLabel>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="연락처"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>운영중인 유튜브 채널 (필수)</FormLabel>
                <Input
                  name="channel"
                  value={form.channel}
                  onChange={handleChange}
                  placeholder="유튜브 채널"
                />
              </FormControl>
              <Checkbox
                name="agree"
                isChecked={form.agree}
                onChange={handleChange}
                mb={2}
              >
                (필수) 레이블리 이용 상담을 위한 개인정보 제공에 동의합니다.
              </Checkbox>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={isLoadingModal}
                isDisabled={!isFormValid}
                w="100%"
                size="lg"
              >
                상담 요청하기
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh" py={8}>
      <Container maxW="container.xl">
        {/* 상단 요약 카드 */}
        <StatGroup mb={6} gap={4}>
          {stats.map((stat, idx) => (
            <Stat
              key={stat.label}
              px={6}
              py={4}
              bg="white"
              borderRadius="xl"
              boxShadow="md"
              minW="220px"
            >
              <Flex align="center" gap={1}>
                <StatLabel fontSize="md" color="gray.600">
                  {stat.label}
                </StatLabel>
                {stat.hasTooltip && (
                  <Tooltip
                    label="유효 조회수 기준으로 데이터 제공"
                    placement="top"
                    hasArrow
                    bg="gray.800"
                    color="white"
                    borderRadius="md"
                    fontSize="sm"
                    px={3}
                    py={2}
                  >
                    <IconButton
                      icon={<QuestionIcon />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      aria-label="조회수 정보"
                      minW="auto"
                      h="auto"
                      p={1}
                      _hover={{
                        bg: "blue.50",
                        color: "blue.500",
                        transform: "scale(1.1)",
                      }}
                      transition="all 0.2s"
                    />
                  </Tooltip>
                )}
              </Flex>
              <StatNumber fontSize="2xl" color="black">
                {stat.value}
              </StatNumber>
            </Stat>
          ))}
        </StatGroup>

        {/* 기간 필터 */}
        <Flex align="center" gap={4} mb={4}>
          <Select
            value={period}
            onChange={(e) => handlePeriodChange(Number(e.target.value))}
            maxW="120px"
            bg="white"
          >
            <option value={7}>최근 7일</option>
            <option value={14}>최근 14일</option>
            <option value={21}>최근 21일</option>
            <option value={30}>최근 30일</option>
          </Select>
          <Input
            type="date"
            value={inputDateRange.start}
            onChange={(e) =>
              setInputDateRange((r) => ({ ...r, start: e.target.value }))
            }
            maxW="160px"
            bg="white"
          />
          <Text>~</Text>
          <Input
            type="date"
            value={inputDateRange.end}
            onChange={(e) =>
              setInputDateRange((r) => ({ ...r, end: e.target.value }))
            }
            maxW="160px"
            bg="white"
          />
          <Button colorScheme="teal" onClick={handleSearch}>
            검색
          </Button>
        </Flex>

        {/* 첫 번째 차트 */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={4} mb={8}>
          <Chart
            options={chart1.options}
            series={chart1.series}
            type="line"
            height={320}
          />
        </Box>

        {/* 두 번째 차트 */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={4}>
          <Chart
            options={chart2.options}
            series={chart2.series}
            type="line"
            height={320}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
